/**
 * SessionChip — the session token rendered in every calendar view (11E.7).
 *
 * Replaces the Phase-4 chip, which showed `HH:MM · <raw class_id prefix>`.
 * Per the 11E.7 spec a chip now carries: `Subject (booked/capacity)`, a
 * per-staff colour key, an attendance-marked check, and (where there is
 * room) the first few student names plus a "+N more" overflow.
 *
 * Name resolution is the caller's job — the chip takes already-resolved
 * label maps. The calendar owns one lookup query for the whole grid; a
 * chip that fetched its own names would be an N+1 per rendered cell.
 *
 * `density` controls how much detail fits:
 *   - `compact`  → month cells: one line, names only if they fit
 *   - `full`     → week/day cells: always show names
 */

import React from 'react';

import { fmtTimeInTz } from '@features/calendar/utils/tz';
import { CANCELLED_COLOR, staffColor } from '@features/calendar/utils/staffColor';
import type { SessionResponse } from '@features/calendar/api/sessions';

export interface SessionChipProps {
  session: SessionResponse;
  tenantTz: string;
  /** subject_id → display name. Falls back to the id when absent. */
  subjectNames?: Record<string, string>;
  /** student_id → display name, for the roster preview. */
  studentNames?: Record<string, string>;
  density?: 'compact' | 'full';
  onClick?: (e: React.MouseEvent) => void;
  draggable?: boolean;
}

/** How many student names to print before collapsing into "+N more". */
const NAME_PREVIEW_LIMIT = 3;

function buildRosterPreview(
  studentIds: string[],
  studentNames: Record<string, string> | undefined,
  limit: number,
): string {
  if (studentIds.length === 0) return '';
  const shown = studentIds.slice(0, limit).map((id) => studentNames?.[id] ?? id);
  const remaining = studentIds.length - shown.length;
  return remaining > 0 ? `${shown.join(', ')} +${remaining} more` : shown.join(', ');
}

export const SessionChip: React.FC<SessionChipProps> = ({
  session,
  tenantTz,
  subjectNames,
  studentNames,
  density = 'full',
  onClick,
  draggable = true,
}) => {
  const isCancelled = session.status === 'cancelled';
  const colors = isCancelled ? CANCELLED_COLOR : staffColor(session.teacher_id);

  const subjectLabel = subjectNames?.[session.subject_id] ?? session.subject_id;
  const studentIds = session.student_ids ?? [];
  const booked = session.booked_count ?? studentIds.length;
  // Capacity is null on sessions materialised before 11E.7 — show the
  // booked count alone rather than a misleading "2/0".
  const capacityLabel = session.capacity != null ? `${booked}/${session.capacity}` : `${booked}`;
  const attendanceMarked = Boolean(session.attendance_marked_at);

  const rosterPreview =
    density === 'full'
      ? buildRosterPreview(studentIds, studentNames, NAME_PREVIEW_LIMIT)
      : buildRosterPreview(studentIds, studentNames, 2);

  const title = [
    `${subjectLabel} (${capacityLabel})`,
    fmtTimeInTz(session.starts_at, tenantTz),
    attendanceMarked ? 'Attendance marked' : null,
    rosterPreview || null,
  ]
    .filter(Boolean)
    .join(' — ');

  return (
    <div
      // Legacy Phase-4 specs (schedule/cancel/conflicts) locate chips with
      // `[data-testid^="candeza-calendar-session-"]` — a PREFIX match. Any
      // testid inside this component that shares that prefix (e.g. an
      // earlier `candeza-calendar-session-label-*` on the child span below)
      // gets counted as a second chip and silently doubles those specs'
      // counts. Everything nested inside the chip uses `candeza-calendar-
      // chip-*` instead, precisely to not share this prefix.
      data-testid={`candeza-calendar-session-${session.id}`}
      data-flow="calendar-session-event"
      data-session-status={session.status}
      data-attendance-marked={attendanceMarked ? 'true' : 'false'}
      data-booked={String(booked)}
      data-capacity={session.capacity != null ? String(session.capacity) : ''}
      draggable={draggable && !isCancelled}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/session-id', session.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onClick}
      title={title}
      style={{
        background: colors.bg,
        color: colors.fg,
        borderLeft: `3px solid ${colors.accent}`,
        borderRadius: 4,
        padding: density === 'compact' ? '2px 4px' : '3px 6px',
        fontSize: density === 'compact' ? 10 : 11,
        lineHeight: 1.3,
        cursor: 'pointer',
        overflow: 'hidden',
        textDecoration: isCancelled ? 'line-through' : 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontWeight: 600,
        }}
      >
        {density === 'full' && (
          <span style={{ opacity: 0.75, fontWeight: 400 }}>
            {fmtTimeInTz(session.starts_at, tenantTz)}
          </span>
        )}
        <span
          style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
          data-testid={`candeza-calendar-chip-label-${session.id}`}
        >
          {subjectLabel} ({capacityLabel})
        </span>
        {attendanceMarked && (
          <span
            aria-label="Attendance marked"
            data-testid={`candeza-calendar-chip-attendance-${session.id}`}
            style={{ marginLeft: 'auto', flexShrink: 0 }}
          >
            ✓
          </span>
        )}
      </div>

      {rosterPreview && (
        <div
          data-testid={`candeza-calendar-chip-roster-${session.id}`}
          style={{
            fontSize: density === 'compact' ? 9 : 10,
            opacity: 0.8,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 400,
          }}
        >
          {rosterPreview}
        </div>
      )}
    </div>
  );
};
